import type { LocationBreadcrumb } from "@ots-frontend/ots-data";
import { useMemo } from "react";
import { OtsGoogleMap, type OtsMapMarker, type OtsMapPath, otsMapPosition } from "../components/OtsGoogleMap";
import type { ActivityEntry } from "./driver-activity-view-model";

export type ActivityRoutePoint = {
  id: string;
  kind: "pickup" | "delivery" | "pickup-geofence" | "delivery-geofence";
  label: string;
  position: { lat: number; lng: number };
};

const at = otsMapPosition;

/** Where a ticket happened: where each side was written, and where its matched geofence visit was recorded. */
export const getActivityRoutePoints = (entry: ActivityEntry): ActivityRoutePoint[] => {
  const points: ActivityRoutePoint[] = [];
  const add = (id: string, kind: ActivityRoutePoint["kind"], label: string, position?: { lat: number; lng: number }) => {
    if (position) points.push({ id, kind, label, position });
  };
  const { pickup, delivery, pickupGeofence, deliveryGeofence } = entry;
  add("pickup", "pickup", `Pickup · ${pickup?.siteName ?? "ticket"}`, at(pickup?.latCoord, pickup?.longCoord));
  add("pickup-geofence", "pickup-geofence", `Pickup geofence · ${pickupGeofence?.event.siteName ?? pickup?.siteName ?? "visit"}`, at(pickupGeofence?.event.latCoord, pickupGeofence?.event.longCoord));
  add("delivery", "delivery", `Delivery · ${delivery?.siteName ?? "ticket"}`, at(delivery?.latCoord, delivery?.longCoord));
  add("delivery-geofence", "delivery-geofence", `Delivery geofence · ${deliveryGeofence?.event.siteName ?? delivery?.siteName ?? "visit"}`, at(deliveryGeofence?.event.latCoord, deliveryGeofence?.event.longCoord));
  return points;
};

/** One recorded position on the driver's path. */
export type ActivityBreadcrumb = { lat: number; lng: number; time: number };

/** The driver's recorded path for a ticket (`/tickets/{id}/gps-breadcrumbs`), oldest first, without empty fixes. */
export const toActivityBreadcrumbs = (breadcrumbs: LocationBreadcrumb[] | undefined): ActivityBreadcrumb[] =>
  (breadcrumbs ?? [])
    .filter((breadcrumb) => breadcrumb.latitude !== 0 || breadcrumb.longitude !== 0)
    .map((breadcrumb) => ({ lat: breadcrumb.latitude, lng: breadcrumb.longitude, time: breadcrumb.dttm }))
    .sort((left, right) => left.time - right.time);

const markerColors: Record<ActivityRoutePoint["kind"], string> = {
  pickup: "#16a34a",
  delivery: "#dc2626",
  "pickup-geofence": "#86efac",
  "delivery-geofence": "#fca5a5",
};

export type ActivityRouteMapProps = {
  entry: ActivityEntry;
  /** The host's Google Maps key; the map explains itself without one. */
  apiKey?: string;
  /** The driver's recorded path, drawn as the road taken; see `ConnectedActivityRouteMap`. */
  breadcrumbs?: ActivityBreadcrumb[];
  className?: string;
};

const NO_BREADCRUMBS: ActivityBreadcrumb[] = [];

/**
 * A ticket on the map: pickup (green) to delivery (red), with the matched geofence visits in the paler
 * shades, so a pin far from its geofence is obvious without leaving the screen.
 */
export const ActivityRouteMap = ({ entry, apiKey, breadcrumbs = NO_BREADCRUMBS, className }: ActivityRouteMapProps) => {
  const points = useMemo(() => getActivityRoutePoints(entry), [entry]);
  const markers = useMemo<OtsMapMarker[]>(() => points.map((point) => ({
    id: point.id,
    position: point.position,
    label: point.label,
    color: markerColors[point.kind],
    size: point.kind.endsWith("geofence") ? 6 : 9,
  })), [points]);
  const paths = useMemo<OtsMapPath[]>(() => {
    const road = breadcrumbs.map(({ lat, lng }) => ({ lat, lng }));
    if (road.length >= 2) return [{ id: "breadcrumbs", path: road, color: "#38bdf8" }];
    // Without a recorded road, a dashed line pairs the two tickets: not the road the truck took.
    const route = points.filter((point) => point.kind === "pickup" || point.kind === "delivery").map((point) => point.position);
    return route.length === 2 ? [{ id: "pairing", path: route, color: "#ffffff", dashed: true }] : [];
  }, [breadcrumbs, points]);
  return (
    <OtsGoogleMap
      apiKey={apiKey}
      markers={markers}
      paths={paths}
      emptyMessage="No location was recorded for this ticket."
      subject="this ticket"
      className={["h-56", className].filter(Boolean).join(" ")}/>
  );
};
